import type { GooglePlace } from "@albertine/types";
import { GooglePlaceLocationSearchFields } from "../../common/utils/google/place.util";
import { placeToLocation } from "../../common/utils/theme.util";
import { useThemesUrlQuery } from "../../utils/customHook.util";
import { debounce } from "../../utils/ratelimit.util";
import { Flex } from "../Common/Flex";
import Input from "../Common/Input";
import GooglePlaceInputField, {
    useGooglePlaceInputRef,
} from "../GooglePlaceInputField/GooglePlaceInputField";
import "./ThemeFilters.css";
import { VisibilityFilter, VisibilityFilterTextMap } from "@albertine/types";
import { TenantSchema } from "@albertine/types/openapi/lmt";
import {
    ThemePreference,
    ThemePreferenceTextMap,
    themeLocationToText,
} from "@albertine/types/theme";
import type { TenantPointOfView } from "@/common/types/DynamicTheme";
import { formatTenantName } from "@/common/utils/tenant.util";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { isTypeOf } from "../../common/utils/zod.util";
import Dropdown from "../Common/Dropdown";
import AutocompleteDropdown from "../Common/Input.AutocompleteDropdown";

type PublishedStatus = VisibilityFilter | "all";

function ThemeFilters() {
    const { urlState, setUrlState } = useThemesUrlQuery();

    const googlePlaceInputField = useGooglePlaceInputRef({
        initialValue: urlState.location
            ? themeLocationToText(urlState.location)
            : undefined,
    });

    const onNameChange = debounce((searchTerm: string) => {
        setUrlState({ name: searchTerm.length !== 0 ? searchTerm : undefined });
    }, 200);

    const onLocationClear = () => {
        setUrlState({
            country: undefined,
            city: undefined,
            countryCode: undefined,
        });
    };

    const onLocationSelect = (place: GooglePlace) => {
        const location = placeToLocation(place);

        setUrlState({
            country: location?.country || undefined,
            city: location?.city || undefined,
            countryCode: location?.countryCode || undefined,
        });
    };

    const onPreferenceSelect = (preferences: ThemePreference[]) => {
        setUrlState({
            preferences,
        });
    };

    const onTenantPointOfViewSelect = (tenantPointOfView: TenantPointOfView) => {
        setUrlState({
            tenantPointOfView,
        });
    };

    const onPublishedStatusSelect = (published: PublishedStatus) => {
        if (published === "all") {
            setUrlState({
                published: undefined,
            });
        } else {
            setUrlState({
                published,
            });
        }
    };

    const tenantDropdownItems = TenantSchema.options.reduce(
        (acc, it) => {
            acc[it] = formatTenantName(it);
            return acc;
        },
        { global: "All" } as Record<TenantPointOfView, string>,
    );

    return (
        <Flex.Row gap="medium" className="theme-filters">
            <div className="theme-filter">
                <Input.PersistentTextField
                    label="Theme name"
                    placeholder="Search"
                    value={urlState.nameSearchTerm || ""}
                    optionToClear
                    onChange={onNameChange}
                />
            </div>

            <div className="theme-filter">
                <GooglePlaceInputField
                    label="Location targeting"
                    placeholder="All"
                    ref={googlePlaceInputField}
                    searchFor="citiesAndCountriesOnly"
                    fields={GooglePlaceLocationSearchFields}
                    onSelect={onLocationSelect}
                    onClear={onLocationClear}
                />
            </div>

            <div className="theme-filter">
                <AutocompleteDropdown
                    label="Preference targeting"
                    placeholder="All"
                    items={ThemePreference.options.map((it) => ({
                        id: it,
                        text: ThemePreferenceTextMap[it] || it,
                        selected: !!urlState.preferences?.includes(it),
                    }))}
                    onChange={(newSelections) => {
                        onPreferenceSelect(newSelections.map((it) => it.id));
                    }}
                />
            </div>

            <div className="theme-filter">
                <Select
                    items={tenantDropdownItems}
                    value={urlState.tenantPointOfView || "global"}
                    onValueChange={(value) => {
                        if (value) onTenantPointOfViewSelect(value);
                    }}
                >
                    <SelectTrigger className="h-auto min-h-[46px] w-full rounded-none border-zinc-200 bg-white px-2 pt-1 pb-1.5 shadow-none hover:border-zinc-400 focus-visible:border-zinc-400 focus-visible:ring-0 data-[size=default]:h-auto">
                        <span className="flex flex-col items-start gap-0.5">
                            <span className="text-xs text-zinc-500">Tenant</span>
                            <SelectValue />
                        </span>
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="global">All</SelectItem>
                        {TenantSchema.options.map((it) => (
                            <SelectItem
                                key={it}
                                value={it}
                                label={formatTenantName(it)}
                            >
                                {formatTenantName(it)}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="theme-filter">
                <Dropdown
                    label="Published status"
                    optionList={["all", ...VisibilityFilter.options].map((it) => ({
                        label: isTypeOf(it, VisibilityFilter)
                            ? VisibilityFilterTextMap[it]
                            : "All",
                        value: it,
                    }))}
                    currentValue={urlState.published || "all"}
                    onChange={(published) => {
                        onPublishedStatusSelect(
                            (published || "all") as PublishedStatus,
                        );
                    }}
                />
            </div>
        </Flex.Row>
    );
}

export default ThemeFilters;
